/**
 * NotificationBell — Header button that shows unread notification count and
 * toggles the NotificationPanel.
 *
 * Design: ADR-2 / design §2.6
 *
 * Accessibility:
 *   - aria-label describes current state including unread count.
 *   - aria-expanded reflects panel open/closed.
 *   - min-h-[44px] WCAG 2.1 AA touch target.
 *   - No innerHTML with data from API.
 */

interface NotificationBellProps {
  /** Number of unread notifications. */
  unreadCount: number;
  /** Whether the notification panel is currently open. */
  isOpen: boolean;
  /** Toggle callback. */
  onToggle: () => void;
}

export function NotificationBell({
  unreadCount,
  isOpen,
  onToggle,
}: NotificationBellProps): JSX.Element {
  const label =
    unreadCount > 0
      ? `Notificaciones (${unreadCount} sin leer)`
      : "Notificaciones";

  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={isOpen}
      aria-haspopup="true"
      onClick={onToggle}
      className="relative min-h-[44px] min-w-[44px] rounded-lg border border-zinc-700 px-2 text-zinc-300 transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
    >
      {/* Bell icon — inline SVG, no external resource */}
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        className="mx-auto h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>

      {/* Unread badge */}
      {unreadCount > 0 ? (
        <span
          aria-hidden="true"
          className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] font-bold text-white"
        >
          {unreadCount > 99 ? "99+" : String(unreadCount)}
        </span>
      ) : null}
    </button>
  );
}
